import React, { useState, useCallback, useMemo, useEffect } from 'react';
import RightRail, { Header, Content, Footer } from '../../../libs/gs-component-right-rail';
import { MultiSelectGroup } from '@xo-union/tk-component-switches';
import { Button } from '@xo-union/tk-component-buttons';
import { H4 } from '@xo-union/tk-ui-typography';
import { DisplayButton } from '@xo-union/tk-ui-links';
import { PillDropdownOptions } from './index';
import { CheckBoxOptionsList } from './Dropdown';
import styles from './styles.scss';

export interface FilterValue {
  colors?: string[];
  styles?: string[];
  seasons?: string[];
}

interface filterOptions {
  colors: PillDropdownOptions;
  styles: PillDropdownOptions;
  seasons: PillDropdownOptions;
}

interface MobileGuestFiltersProps {
  options: filterOptions;
  defaultValue: FilterValue;
  onClose: () => void;
  onSave: (selectedValues: FilterValue) => void;
  isOpen: boolean;
}

export const MobileDropdown = ({ options, defaultValue, onSave, onClose, isOpen }: MobileGuestFiltersProps) => {
  const [selectedColors, setSelectedColors] = useState(defaultValue.colors || []);
  const [selectedStyles, setSelectedStyles] = useState(defaultValue.styles || []);
  const [selectedSeasons, setSelectedSeasons] = useState(defaultValue.seasons || []);
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    setSelectedColors(defaultValue.colors || []);
    setSelectedStyles(defaultValue.styles || []);
    setSelectedSeasons(defaultValue.seasons || []);
  }, [defaultValue]);

  const handleChange = useCallback(
    (selectedValues: string[], filterName: string) => {
      switch (filterName) {
        case 'colors':
          setSelectedColors(selectedValues);
          break;
        case 'styles':
          setSelectedStyles(selectedValues);
          break;
        case 'seasons':
          setSelectedSeasons(selectedValues);
          break;
      }
    },
    [setSelectedColors, setSelectedStyles, setSelectedSeasons]
  );

  const colorOptions = useMemo(() => {
    const remainingOptions = [...options.colors];
    const firstTenOptions = remainingOptions.splice(0, 10);
    return {
      firstTenOptions,
      remainingOptions,
    };
  }, [options.colors]);

  const handleOnSave = useCallback(() => {
    onSave({ colors: selectedColors, styles: selectedStyles, seasons: selectedSeasons });
  }, [selectedColors, selectedStyles, selectedSeasons, onSave]);

  const render_options = useCallback(
    () => (
      <>
        <div className={styles.mobileFilterSection}>
          <H4 as="h1">Styles</H4>
          <MultiSelectGroup values={selectedStyles} onValuesChange={(value: string[]) => handleChange(value, 'styles')}>
            <CheckBoxOptionsList options={options.styles} />
          </MultiSelectGroup>
        </div>
        <div className={styles.mobileFilterSection}>
          <H4 as="h1">Colors</H4>
          <MultiSelectGroup values={selectedColors} onValuesChange={(value: string[]) => handleChange(value, 'colors')}>
            <CheckBoxOptionsList options={colorOptions.firstTenOptions} />
            {showMore ? (
              <div className={styles.moreOptionsContainer}>
                <CheckBoxOptionsList options={colorOptions.remainingOptions} />
              </div>
            ) : (
              <DisplayButton
                className={styles.showMore}
                onClick={() => setShowMore(true)}
                data-testid="show-more-colors-btn"
                size="md"
              >
                Show more colors
              </DisplayButton>
            )}
          </MultiSelectGroup>
        </div>
        <div className={styles.mobileFilterSection}>
          <H4 as="h1">Seasons</H4>
          <MultiSelectGroup
            values={selectedSeasons}
            onValuesChange={(value: string[]) => handleChange(value, 'seasons')}
          >
            <CheckBoxOptionsList options={options.seasons} />
          </MultiSelectGroup>
        </div>
      </>
    ),
    [selectedColors, selectedSeasons, selectedStyles, showMore, options, colorOptions, handleChange]
  );

  return (
    <RightRail open={isOpen} overlay={false} className={styles.mobileRightRail}>
      <Header onClose={onClose}>Filters</Header>
      <Content>{render_options()}</Content>
      <Footer>
        <Button
          className={styles.mobileButton}
          block="sm"
          color="primary"
          size="md"
          onClick={handleOnSave}
          type="button"
          data-testid="mobile-filters-save-btn"
        >
          See designs
        </Button>
      </Footer>
    </RightRail>
  );
};
